<template>
    <div>
        <template v-if="tasksLoading">
            <h1><div class="count-blinker"></div> opgaver</h1>
        </template>
        <template v-else-if="tasks && tasks.length > 0">
            <h1>{{ tasks[0].rowCount }} opgave<template v-if="tasks.length > 1">r</template></h1>
        </template>
        <template v-else>
            <h1>Ingen opgaver</h1>
        </template>

        <div class="list-filter" v-if="activeFilters">
            <div class="list-filter-group">
                <template v-if="supplyTypes && supplyTypes.length > 1">
                    <div class="list-filter-item" v-for="supplyType in supplyTypes" :key="supplyType.sysBusinessAreaTypeId" v-if="getFilters.supplyType.includes(supplyType.sysBusinessAreaTypeId)" :class="'list-filter-item--' + supplyType.systemName">
                        <span class="list-filter-item-label">Forretningsområde:</span><span class="list-filter-item-value"><icon class="tiny-svg" :name="supplyType.systemName" />{{ supplyType.description }}</span>
                        <button class="list-filter-item-button" @click="removeSupplyTypeFilter(supplyType.sysBusinessAreaTypeId)">
                            <span class="visually-hidden">Fjern filter</span>
                            <span aria-hidden="true">&#10761;</span>
                        </button>
                    </div>
                </template>

                <div class="list-filter-item" v-if="getFilters.address">
                    <span class="list-filter-item-label">Adresse:</span><span class="list-filter-item-value">{{ getFilters.address }}</span>
                    <button class="list-filter-item-button" @click="removeAddressFilter()">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>

                <div class="list-filter-item list-filter-item--alert" v-if="getFilters.kpi === 'tasksReadyForApproval'">
                    <span class="list-filter-item-value">Opgaver færdigmeldt</span>
                    <button class="list-filter-item-button" @click="removeKPIFilter()">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>

                <div class="list-filter-item" v-if="getFilters.tasks.showClosed">
                    <span class="list-filter-item-label">Status:</span><span class="list-filter-item-value">Vis også afsluttede</span>
                    <button class="list-filter-item-button" @click="removeTaskFilter('showClosed')">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>

                <div class="list-filter-item" v-if="getFilters.tasks.showReadyForApproval">
                    <span class="list-filter-item-label">Status:</span><span class="list-filter-item-value">Vis kun færdigmeldte</span>
                    <button class="list-filter-item-button" @click="removeTaskFilter('showReadyForApproval')">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>

                <div class="list-filter-item" v-for="responsible in getFilters.tasks.responsible" :key="responsible">
                    <span class="list-filter-item-label">Ansvarlig:</span><span class="list-filter-item-value">{{ caseworkers.find(item => item.userId === responsible).name }}</span>
                    <button class="list-filter-item-button" @click="removeTaskFilter('responsible', responsible)">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>

                <div class="list-filter-item" v-for="company in getFilters.tasks.company" :key="company">
                    <span class="list-filter-item-label">Virksomhed:</span><span class="list-filter-item-value">{{ firms.find(item => item.id === company).name }}</span>
                    <button class="list-filter-item-button" @click="removeTaskFilter('company', company)">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>

                <div class="list-filter-item" v-for="contact in getFilters.tasks.contact" v-if="users.find(item => item.userId === contact)" :key="contact">
                    <span class="list-filter-item-label">Medarbejder:</span><span class="list-filter-item-value">{{ users.find(item => item.userId === contact).name }}</span>
                    <button class="list-filter-item-button" @click="removeTaskFilter('contact', contact)">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>

                <div class="list-filter-item" v-if="getFilters.tasks.requisitionsSerialNumber">
                    <span class="list-filter-item-label">Løbenummer:</span><span class="list-filter-item-value">{{ getFilters.tasks.requisitionsSerialNumber }}</span>
                    <button class="list-filter-item-button" @click="removeTaskFilter('requisitionsSerialNumber')">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>

                <div class="list-filter-item" v-if="getFilters.tasks.name">
                    <span class="list-filter-item-label">Opgavenavn:</span><span class="list-filter-item-value">{{ getFilters.tasks.name }}</span>
                    <button class="list-filter-item-button" @click="removeTaskFilter('name')">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>

                <div class="list-filter-item" v-if="getFilters.tasks.startDate.from || getFilters.tasks.startDate.to">
                    <span class="list-filter-item-label">Startdato<template v-if="getFilters.tasks.startDate.from && !getFilters.tasks.startDate.to"> efter</template><template v-if="!getFilters.tasks.startDate.from && getFilters.tasks.startDate.to"> før</template>:</span>
                    <span class="list-filter-item-value">
                        <template v-if="getFilters.tasks.startDate.from && getFilters.tasks.startDate.to">
                            {{ getFilters.tasks.startDate.from | formatDateShort }} - {{ getFilters.tasks.startDate.to | formatDateShort }}
                        </template>
                        <template v-if="getFilters.tasks.startDate.from && !getFilters.tasks.startDate.to">
                            {{ getFilters.tasks.startDate.from | formatDateShort }}
                        </template>
                        <template v-if="!getFilters.tasks.startDate.from && getFilters.tasks.startDate.to">
                            {{ getFilters.tasks.startDate.to | formatDateShort }}
                        </template>
                    </span>
                    <button class="list-filter-item-button" @click="removeTaskFilter('startDate', getFilters.tasks.startDate.from || getFilters.tasks.startDate.to)">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>
            </div>
        </div>

        <pagination v-show="tasks && tasks.length > 0 && tasks[0].pageCount > 1 && !tasksLoading" :showPaging="tasks && tasks.length > 0 && tasks[0].pageCount > 1 ? true : false" :totalPages="tasks && tasks.length > 0 ? tasks[0].pageCount : null" :page="'tasks'" :position="'right'" />
        <div v-if="tasks && tasks.length > 0 && !tasksLoading" class="grid-x list-sort">
            <div class="cell large-1"></div>
            <!-- Makes no sense that this sorting parameter is address1, but the backend people demands this :) -->
            <div class="cell large-2">
                <button :aria-selected="currentSort === 'address1'" @click="sort('address1')">Navn<icon v-if="currentSort === 'address1'" class="small-svg fill-selected-dark-gray" :class="{rotated: !currentSortDir && currentSort === 'address1'}" name="keyboard_arrow_sort" /></button>
            </div>
            <div class="cell large-2">
                <button :aria-selected="currentSort === 'requisitionedCount'" @click="sort('requisitionedCount')">Rekvirerede aktiviteter<icon v-if="currentSort === 'requisitionedCount'" class="small-svg fill-selected-dark-gray" :class="{rotated: !currentSortDir && currentSort === 'requisitionedCount'}" name="keyboard_arrow_sort" /></button>
            </div>
            <div class="cell large-2">
                <button :aria-selected="currentSort === 'updated'" @click="sort('updated')">Startdato<icon v-if="currentSort === 'updated'" class="small-svg fill-selected-dark-gray" :class="{rotated: !currentSortDir && currentSort === 'updated'}" name="keyboard_arrow_sort" /></button>
            </div>
            <div class="cell large-2">
                Entreprenør
            </div>
            <div class="cell large-2">
                <button :aria-selected="currentSort === 'ownerUser'" @click="sort('ownerUser')">Ansvarlig<icon v-if="currentSort === 'ownerUser'" class="small-svg fill-selected-dark-gray" :class="{rotated: !currentSortDir && currentSort === 'ownerUser'}" name="keyboard_arrow_sort" /></button>
            </div>
            <div class="cell large-1">
                <button :aria-selected="currentSort === 'status'" @click="sort('status')">Status<icon v-if="currentSort === 'status'" class="small-svg fill-selected-dark-gray" :class="{rotated: !currentSortDir && currentSort === 'status'}" name="keyboard_arrow_sort" /></button>
            </div>
        </div>
        <transition-group name="list" tag="div" v-if="!tasksLoading">
            <div class="grid-x list-row supply align-items" :class="businessAreaClass(task.businessAreaTypeId)" v-for="task in tasks" :key="task.taskId">
                <div class="cell large-1">
                    <icon class="medium-svg fill-primary" :name="task.hasActivitiesFromRoutine ? 'task-routine' : 'task'" />
                </div>
                <div class="cell large-2 item truncate-fade">
                    <span>{{ task.taskName }}</span>
                    <span v-if="task.address1">{{ task.address1 }}<span v-if="task.address2">, {{ task.address2 }},</span> {{ task.postalCode }} {{ task.city }}</span>
                    <span v-else>Ingen adresse</span>
                </div>
                <div class="cell large-2 item truncate-fade">
                    <span v-if="task.requisitionedCount">{{ task.requisitionedCount }} rekvirere<span v-if="task.requisitionedCount === 1">t</span><span v-if="task.requisitionedCount !== 1">de</span> aktivitet<span v-if="task.requisitionedCount > 1">er</span></span>
                    <span v-else>Ingen rekvirerede aktiviteter</span>
                    <span v-if="task.requestCount">{{ task.requestCount }} henvendelse<span v-if="task.requestCount > 1">r</span></span>
                    <span v-else>Ingen henvendelser</span>
                </div>
                <div class="cell large-2 item truncate-fade">
                    <!-- <span v-if="task.nextLatestDate">Slutdato</span> -->
                    <span v-if="task.nextStartDate">{{ task.nextStartDate | formatDateShortElapsed }}</span>
                    <span v-if="task.nextStartDate">{{ task.nextStartDate | formatTime }}</span>
                    <span v-else>Ingen dato</span>
                </div>
                <div class="cell large-2 item truncate-fade">
                    <span>{{task.contractorList}}</span>
                    <span>{{task.contractorCount}} entreprenør<span v-if="task.contractorCount !== 1">er</span></span>
                </div>
                <div class="cell large-2 item truncate-fade">
                    <span>{{ task.ownerUser }}</span>
                    <span v-if="!task.ownerActive"><em>Inaktiv bruger</em></span>
                    <span v-else>{{ task.ownerOrganization }}</span>
                </div>
                <div class="cell large-1 item flexbox-center">
                    <div class="task-status" :class="statusColor(task.status)">{{ task.status }}</div>
                </div>
                <router-link class="dashboard-list-link" :to="{ name: 'taskid', params: { id: task.taskId } }">
                    <span class="visually-hidden">Åben henvendelse</span>
                </router-link>
            </div>
        </transition-group>
        <pagination v-show="tasks && tasks.length > 0 && tasks[0].pageCount > 1 && !tasksLoading" :showPaging="tasks && tasks.length > 0 && tasks[0].pageCount > 1 ? true : false" :totalPages="tasks && tasks.length > 0 ? tasks[0].pageCount : null" :page="'tasks'" :position="'right'" />

        <div class="dashboard-list-empty" v-if="!tasksLoading && tasks && tasks.length === 0">
            <template v-if="activeFilters">
                <h2>Ingen opgaver, prøv at ændre en af følgende ting:</h2>
                <ul>
                    <li v-if="getFilters.supplyType.length < supplyTypes.length">Vælg et andet forretningsområde</li>
                    <li v-if="getFilters.tasks.company.length > 1 || getFilters.tasks.contact.length > 1 || getFilters.tasks.responsible.length > 1">Vælg en anden ansvarlig, virksomhed eller entreprenør</li>
                    <li v-if="getFilters.address">Fjern søgning efter adresse</li>
                    <li>Fjern et eller flere filtre</li>
                </ul>
            </template>
            <template v-else>
                <p><em>Ingen opgaver oprettet endnu...</em></p>
            </template>
        </div>

        <div class="text-center dashboard-list-loading" v-if="tasksLoading || !tasks">
            Indlæser opgaver...
            <div class="list-loader">
                <div class="bar"></div>
                <div class="bar"></div>
                <div class="bar"></div>
                <div class="visually-hidden">Indlæser...</div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import pagination from './../../../components/shared/pagination/pagination.vue'

export default {
    name: 'list-tasks',
    components: {
        pagination
    },
    props: {
        value: Array
    },
    data () {
        return {
            tasks: this.value
        }
    },
    computed: {
        ...mapGetters({
            currentSortDir: 'getCurrentTaskOrderByDescending',
            currentSort: 'getCurrentTaskOrderByColumnName',

            tasksLoading: 'getTaskLoading',

            // Filters
            supplyTypes: 'getBusinessAreaTypes',
            caseworkers: 'caseWorkerList',
            firms: 'getAllFirmsForActivity',
            users: 'getAllUsersInFirmForActivity',
            getFilters: 'getFilters'
        }),

        supplyTypesFormatted () {
            let formatted = ''

            this.supplyTypes.forEach((supplyType, index) => {
                if (index > 0) {
                    formatted += ', '
                }
                formatted += supplyType.description
            })

            return formatted
        },
        activeFilters () {
            if (this.getFilters.kpi === 'tasksReadyForApproval' || this.supplyTypes.length > 1 || this.getFilters.address || this.getFilters.tasks.name || (this.getFilters.tasks.responsible && this.getFilters.tasks.responsible.length > 0) || this.getFilters.tasks.showClosed || (this.getFilters.tasks.contact && this.getFilters.tasks.contact.length > 0) || (this.getFilters.tasks.company && this.getFilters.tasks.company.length > 0) || this.getFilters.tasks.startDate.from || this.getFilters.tasks.startDate.to || this.getFilters.tasks.showReadyForApproval) {
                return true
            }

            return false
        }
    },
    watch: {
        value (tasks) {
            this.tasks = tasks
        },

        getFilters: {
            deep: true,
            handler () {
                this.getFilteredList()
            }
        }
    },
    methods: {
        ...mapActions({
            getTasks: 'getQueryTaskList',
            setActivePages: 'setActivePages',
            removeFilters: 'removeFilters',
            resetFilters: 'resetFilters'
        }),

        sort (property) {
            let sort = this.currentSortDir
            const filterObject = {
                orderByDescending: this.currentSort !== property ? false : sort = !sort,
                orderByColumnName: property,
                tab: 'tasks',
                page: 1
            }

            this.setActivePages(filterObject)
        },
        businessAreaClass (businessAreaType) {
            return this.supplyTypes.find(supplyType => supplyType.sysBusinessAreaTypeId === businessAreaType).systemName
        },
        statusColor (status) {
            if (status) {
                if (status.toLowerCase() === 'i gang') {
                    return 'blue-status'
                }
                if (status.toLowerCase() === 'delvist færdigmeldt') {
                    return 'green-blue-status'
                }
                if (status.toLowerCase() === 'færdigmeldt') {
                    return 'green-status'
                }
                if (status.toLowerCase() === 'afsluttet') {
                    return 'gray-status'
                }
            }
            return 'blue-status'
        },
        getFilteredList () {
            const filter = this.getFilters.tasks
            const taskQuery = {}

            if (filter.name) {
                taskQuery.whereLikeTaskName = filter.name
            }
            if (filter.responsible) {
                taskQuery.whereOwnerUserIdList = filter.responsible
            }
            if (filter.showClosed) {
                taskQuery.whereIsClosed = filter.showClosed
            }
            if (filter.contact) {
                taskQuery.whereSentToUserIdList = filter.contact
            }
            if (filter.company) {
                taskQuery.whereSentToOrganizationIdList = filter.company
            }
            if (filter.startDate.from) {
                taskQuery.whereAfterStartDate = filter.startDate.from
            }
            if (filter.startDate.to) {
                taskQuery.whereBeforeLatestDate = filter.startDate.to
            }
            if (filter.showReadyForApproval) {
                taskQuery.whereReadyForApproval = filter.showReadyForApproval
            }

            if (this.getFilters.kpi) {
                taskQuery.kpiFilter = this.getFilters.kpi
            }

            if (this.getFilters.supplyType) {
                taskQuery.whereBusinessAreaTypeIdList = this.getFilters.supplyType
            }

            if (this.getFilters.address) {
                taskQuery.whereLikeAddress = this.getFilters.address
            }

            this.getTasks(taskQuery)
        },
        removeSupplyTypeFilter (value) {
            if (this.getFilters.supplyType.length === 1) {
                this.$notify({
                    group: 'info',
                    type: 'warn',
                    text: 'Der skal være valgt minimum et forretningsområde.',
                    duration: 7500
                })
                return
            }

            this.removeFilters({
                filter: 'supplyType',
                value: value
            })
        },
        removeAddressFilter () {
            this.removeFilters({
                filter: 'address',
                value: ''
            })
        },
        removeKPIFilter () {
            this.resetFilters('user')
        },
        removeTaskFilter (filter, value) {
            if (Array.isArray(this.getFilters.tasks[filter]) && filter === 'responsible') {
                const element = this.$parent.$children.find(child => child.$options.name === 'dashboard-filters').$refs[`enquiry${filter[0].toUpperCase() + filter.slice(1)}`]

                if (element) {
                    element.checked = element.checked.filter(item => item.userId !== value)
                }
            }
            if (typeof this.getFilters.tasks[filter] === 'string' && filter === 'name') {
                this.$parent.$children.find(child => child.$options.name === 'dashboard-filters').$refs.taskNameSearch.value = ''
            }
            this.removeFilters({
                page: 'tasks',
                filter: filter,
                value: value
            })
        }
    },
    mounted () {
        this.getFilteredList()
    }
}
</script>



// WEBPACK FOOTER //
// src/components/shared/list/list-tasks.vue